import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import type { Property, PropertyInsert } from "@/hooks/useProperties";

interface PropertyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  property?: Property;
  onSubmit: (data: PropertyInsert) => Promise<void>;
}

interface FormData {
  title: string;
  address: string;
  city: string;
  postal_code: string;
  property_type: string;
  transaction_type: string;
  surface_m2: string;
  rooms: string;
  price: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  status: string;
}

const propertyTypes = [
  { value: "apartment", label: "Appartement" },
  { value: "house", label: "Maison" },
  { value: "land", label: "Terrain" },
  { value: "commercial", label: "Commerce" },
  { value: "other", label: "Autre" },
];

const transactionTypes = [
  { value: "sale", label: "Vente" },
  { value: "rental", label: "Location" },
];

const statusOptions = [
  { value: "active", label: "Actif" },
  { value: "pending", label: "En attente" },
  { value: "sold", label: "Vendu" },
  { value: "rented", label: "Loué" },
  { value: "archived", label: "Archivé" },
];

export function PropertyDialog({
  open,
  onOpenChange,
  property,
  onSubmit,
}: PropertyDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!property;

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      title: "",
      address: "",
      city: "",
      postal_code: "",
      property_type: "apartment",
      transaction_type: "sale",
      surface_m2: "",
      rooms: "",
      price: "",
      client_name: "",
      client_email: "",
      client_phone: "",
      status: "active",
    },
  });

  // Reset form when property changes or dialog opens
  useEffect(() => {
    if (open) {
      reset({
        title: property?.title || "",
        address: property?.address || "",
        city: property?.city || "",
        postal_code: property?.postal_code || "",
        property_type: property?.property_type || "apartment",
        transaction_type: property?.transaction_type || "sale",
        surface_m2: property?.surface_m2?.toString() || "",
        rooms: property?.rooms?.toString() || "",
        price: property?.price?.toString() || "",
        client_name: property?.client_name || "",
        client_email: property?.client_email || "",
        client_phone: property?.client_phone || "",
        status: property?.status || "active",
      });
    }
  }, [open, property, reset]);

  const onFormSubmit = async (values: FormData) => {
    if (!values.title || !values.address || !values.city) return;
    
    setIsSubmitting(true);
    try {
      await onSubmit({
        title: values.title,
        address: values.address,
        city: values.city,
        postal_code: values.postal_code || null,
        property_type: values.property_type,
        transaction_type: values.transaction_type,
        surface_m2: values.surface_m2 ? Number(values.surface_m2) : null,
        rooms: values.rooms ? Number(values.rooms) : null,
        price: values.price ? Number(values.price) : null,
        client_name: values.client_name || null,
        client_email: values.client_email || null,
        client_phone: values.client_phone || null,
        status: values.status,
        organization_id: null,
      });
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const propertyType = watch("property_type");
  const transactionType = watch("transaction_type");
  const status = watch("status");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Modifier le bien" : "Ajouter un bien"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          {/* Titre */}
          <div className="space-y-2">
            <Label htmlFor="title">Titre du bien *</Label>
            <Input
              id="title"
              placeholder="Appartement T3 Rue de Paris"
              {...register("title", { required: true })}
            />
          </div>

          {/* Adresse */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="address">Adresse *</Label>
              <Input
                id="address"
                placeholder="123 Rue de Paris"
                {...register("address", { required: true })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">Ville *</Label>
              <Input
                id="city"
                placeholder="Paris"
                {...register("city", { required: true })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="postal_code">Code postal</Label>
              <Input
                id="postal_code"
                placeholder="75001"
                {...register("postal_code")}
              />
            </div>
          </div>

          {/* Type et transaction */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type de bien *</Label>
              <Select 
                value={propertyType} 
                onValueChange={(v) => setValue("property_type", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {propertyTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Transaction *</Label>
              <Select 
                value={transactionType} 
                onValueChange={(v) => setValue("transaction_type", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {transactionTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Caractéristiques */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="surface_m2">Surface (m²)</Label>
              <Input
                id="surface_m2"
                type="number"
                placeholder="75"
                {...register("surface_m2")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rooms">Pièces</Label>
              <Input
                id="rooms"
                type="number"
                placeholder="3"
                {...register("rooms")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="price">Prix (€)</Label>
              <Input
                id="price"
                type="number"
                placeholder="250000"
                {...register("price")}
              />
            </div>
          </div>

          {/* Client */}
          <div className="space-y-4 pt-2 border-t">
            <p className="text-sm font-medium text-muted-foreground">Informations client</p>
            
            <div className="space-y-2">
              <Label htmlFor="client_name">Nom du client</Label>
              <Input
                id="client_name"
                placeholder="Jean Dupont"
                {...register("client_name")}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client_email">Email</Label>
                <Input
                  id="client_email"
                  type="email"
                  placeholder="jean@email.com"
                  {...register("client_email")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_phone">Téléphone</Label>
                <Input
                  id="client_phone"
                  placeholder="06 12 34 56 78"
                  {...register("client_phone")}
                />
              </div>
            </div>
          </div>

          {/* Statut (only for editing) */}
          {isEditing && (
            <div className="space-y-2">
              <Label>Statut</Label>
              <Select 
                value={status} 
                onValueChange={(v) => setValue("status", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? "Enregistrer" : "Ajouter"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
